import type {
  KlippekortProductDraft,
  LeadSource,
  OrderDraft,
  PakkelosningProductDraft,
  ProductDraft,
  RateStructure,
  VideoProductDraft,
  WebsiteProductDraft,
  WebsiteType,
  Filmtype,
} from '@jywer/intake';

// `@jywer/intake` exports two `SharePct` types (calculator allows 100 for
// the solo-rep edge case; OrderDraft narrows to 25/50/75). Use the OrderDraft
// shape locally to avoid widening the form state.
export type OrderShare = NonNullable<OrderDraft['extra_sales_rep_share_pct']>;

export type ProductKind = 'website' | 'video' | 'pakkelosning' | 'klippekort';

/**
 * Form-state shape for a single product row. Strings everywhere so the
 * UI can hold partial / unparsed input; `buildProductDraft` converts to
 * the typed `ProductDraft`. Per-type fields live alongside the discriminator
 * — `kind === 'website'` reads `websitePrice`, etc.
 */
export interface ProductFormState {
  kind: ProductKind;
  title: string;
  // website
  websitePrice: string;
  websiteType: WebsiteType;
  // video
  videoPrice: string;
  filmtype: Filmtype | '';
  filmtypeOther: string;
  videoLocations: string;
  shootDates: string[];
  // pakkelosning
  pakkeTotalValue: string;
  pakkeBilledAsSingleLine: boolean;
  pakkeChildren: ProductFormState[];
  // klippekort
  klippekortPrice: string;
  totalHoursPurchased: string;
  // shared by website / video / pakkelosning
  rateStructure: RateStructure;
}

export interface OrderFormState {
  clientName: string;
  clientVat: string;
  isNewClientExplicit: boolean;
  invoiceEmail: string;
  address: string;
  soldAt: string;
  salesRepId: string;
  extraSalesRepId: string;
  extraSalesRepSharePct: '' | OrderShare;
  leadSource: LeadSource;
  emailCustomText: string;
  products: ProductFormState[];
  /**
   * PR #7 — capture signed contract at intake (BUILD_PLAN locked
   * decision #9). The intake POST already accepts
   * `signed_contract_r2_key`; the gap was just the UI. The flow is:
   * (1) user picks a file, (2) UI calls `initContractUpload` +
   * `uploadContract` to push to R2, (3) the resulting r2_key sits in
   * form state until submit, (4) `buildOrderDraft` includes it in
   * the register_order payload, (5) /kunde/{id} Kontrakter sees it
   * via the existing `/api/clients/:id/contracts` reader.
   */
  signedContractR2Key: string | null;
  signedContractFilename: string | null;
}

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function emptyProduct(kind: ProductKind = 'website'): ProductFormState {
  return {
    kind,
    title: '',
    websitePrice: '',
    websiteType: 'custom',
    videoPrice: '',
    filmtype: '',
    filmtypeOther: '',
    videoLocations: '',
    shootDates: [],
    pakkeTotalValue: '',
    pakkeBilledAsSingleLine: false,
    pakkeChildren: [],
    klippekortPrice: '',
    totalHoursPurchased: '',
    rateStructure: '1_rate',
  };
}

export function emptyOrderForm(salesRepId = ''): OrderFormState {
  return {
    clientName: '',
    clientVat: '',
    isNewClientExplicit: false,
    invoiceEmail: '',
    address: '',
    soldAt: todayIso(),
    salesRepId,
    extraSalesRepId: '',
    extraSalesRepSharePct: '',
    leadSource: 'existing_client',
    emailCustomText: '',
    products: [emptyProduct('website')],
    signedContractR2Key: null,
    signedContractFilename: null,
  };
}

function parseAmount(raw: string): number {
  const n = Number((raw ?? '').toString().replace(/,/g, '.'));
  return Number.isFinite(n) ? n : 0;
}

function buildWebsite(p: ProductFormState): WebsiteProductDraft {
  return {
    type: 'website',
    title: p.title || undefined,
    website_price: parseAmount(p.websitePrice),
    website_type: p.websiteType,
    rate_structure: p.rateStructure,
    has_basic_abo: false,
  };
}

function buildVideo(p: ProductFormState): VideoProductDraft {
  const filmtype: Filmtype | undefined = p.filmtype || undefined;
  const draft: VideoProductDraft = {
    type: 'video',
    title: p.title || undefined,
    video_price: parseAmount(p.videoPrice),
    rate_structure: p.rateStructure,
  };
  if (filmtype) draft.filmtype = filmtype;
  if (p.videoLocations.trim()) draft.locations = p.videoLocations.trim();
  const shoots = p.shootDates
    .map((d) => d.trim())
    .filter(Boolean)
    .map((due_date) => ({ due_date }));
  if (shoots.length > 0) draft.shoots = shoots;
  return draft;
}

function buildKlippekort(p: ProductFormState): KlippekortProductDraft {
  return {
    type: 'klippekort',
    title: p.title || undefined,
    klippekort_price: parseAmount(p.klippekortPrice),
    total_hours_purchased: parseAmount(p.totalHoursPurchased),
  };
}

function buildPakkelosning(p: ProductFormState): PakkelosningProductDraft {
  const children: Array<WebsiteProductDraft | VideoProductDraft> = p.pakkeChildren
    .map((c) => {
      if (c.kind === 'website') return buildWebsite(c);
      if (c.kind === 'video') return buildVideo(c);
      return null;
    })
    .filter((c): c is WebsiteProductDraft | VideoProductDraft => c !== null);
  return {
    type: 'pakkelosning',
    title: p.title || undefined,
    total_value: parseAmount(p.pakkeTotalValue),
    rate_structure: p.rateStructure,
    package_billed_as_single_line: p.pakkeBilledAsSingleLine,
    children,
  };
}

export function buildProductDraft(p: ProductFormState): ProductDraft {
  switch (p.kind) {
    case 'website':
      return buildWebsite(p);
    case 'video':
      return buildVideo(p);
    case 'klippekort':
      return buildKlippekort(p);
    case 'pakkelosning':
      return buildPakkelosning(p);
  }
}

/**
 * Converts the UI form state into the wire-shape `OrderDraft` the
 * `/api/intake/register_order` endpoint expects. Empty strings collapse
 * to `undefined` so the server zod-schema treats them as omitted.
 */
export function buildOrderDraft(form: OrderFormState): OrderDraft {
  const draft: OrderDraft = {
    client_name: form.clientName.trim(),
    invoice_email: form.invoiceEmail.trim(),
    lead_source: form.leadSource,
    products: form.products.map(buildProductDraft),
  };
  if (form.clientVat.trim()) draft.client_vat = form.clientVat.trim();
  if (form.isNewClientExplicit) draft.is_new_client_explicit = true;
  if (form.soldAt) draft.sold_at = form.soldAt;
  if (form.salesRepId) draft.sales_rep_id = form.salesRepId;
  if (form.extraSalesRepId) draft.extra_sales_rep_id = form.extraSalesRepId;
  if (form.extraSalesRepSharePct !== '') {
    draft.extra_sales_rep_share_pct = form.extraSalesRepSharePct;
  }
  if (form.emailCustomText.trim()) draft.email_custom_text = form.emailCustomText.trim();
  if (form.signedContractR2Key) draft.signed_contract_r2_key = form.signedContractR2Key;
  return draft;
}
